import { format } from 'date-fns'
import { exists, t, TOptions } from 'i18next'
import React from 'react'

import { DEFAULT_REMORSE_RETURN_DAYS } from '../../common/constants.js'
import {
    ClaimProductResolution,
    InvoicingSystem,
    PlanCode,
    PolicyProvider,
    SubscriptionStatus,
} from '../../common/enums.js'
import { ErrorInSubscriptionDataSimple } from '../../common/errors/subscription-errors.js'
import { SubscriptionProductPlan } from '../../common/models/subscription-product-plan.js'
import {
    PlanPriceDTO,
    SubscriptionDTO,
    SubscriptionModelPermissions,
    SubscriptionProductDTO,
} from '../../common/types.js'
import { calculateNet } from '../../common/utils/maths.js'
import { PlanCardProps } from '../components-hydrated/plan-cards/plan-cards.js'
import { CopyProps } from '../components/copy/copy.js'
import { ChoiceGroupProps } from '../components/forms/choice-group/choice-group.js'
import { NotificationCardProps } from '../components/notification-card/notification-card.js'
import { SubscriptionCardProps } from '../components/subscription-card/subscription-card.js'
import { SubscriptionActionProps, Subscription as SubscriptionItem } from '../components/subscription/subscription.js'
import { UpgradeCardProps } from '../components/upgrade-card/upgrade-card.js'
import i18n from '../i18n.js'
import { State, View } from '../types.js'
import { capture } from '../utils/analytics.js'
import { Customer } from './customer.js'
import { DeliveryMethod } from './delivery-method.js'
import { SubscriptionAccessory } from './subscription-accessory.js'
import { SubscriptionFlexProduct } from './subscription-flex-product.js'
import { SubscriptionLisaAndProduct } from './subscription-lisa-and-product.js'
import { SubscriptionValueProduct } from './subscription-value-product.js'

export interface SubscriptionPrices {
    grossPrice: number
    netPrice: number
}

export type SubscriptionProduct = SubscriptionValueProduct | SubscriptionFlexProduct | SubscriptionLisaAndProduct

function subscriptionProductFactory(product: SubscriptionProductDTO, subscription: Subscription) {
    switch (product.planPrice?.plan?.code) {
        case PlanCode.FLEX: {
            return new SubscriptionFlexProduct(product, subscription)
        }

        case PlanCode.VALUE: {
            return new SubscriptionValueProduct(product, subscription)
        }

        default:
            throw new ErrorInSubscriptionDataSimple('C3-1', 'Unknown PLAN encountered')
    }
}

export class Subscription {
    readonly data: SubscriptionDTO
    readonly deliveryMethods: DeliveryMethod[]
    readonly mainProduct: SubscriptionProduct
    readonly products?: Array<SubscriptionProduct>
    readonly accessories?: SubscriptionAccessory[]
    readonly signedDate?: Date
    readonly lastUpdatedDate?: Date
    readonly contractStartDate?: Date
    readonly contractEndDate?: Date
    readonly permissions?: SubscriptionModelPermissions
    readonly locale: string

    deliveryMethod?: DeliveryMethod
    customer?: Customer

    constructor(subscription: SubscriptionDTO, products?: SubscriptionLisaAndProduct[]) {
        this.data = subscription

        this.locale = subscription.locale?.code || ''
        this.permissions = this.data.permissions
        this.signedDate = this.data.createdAt ? new Date(this.data.createdAt) : undefined
        this.lastUpdatedDate = this.data.updatedAt ? new Date(this.data.updatedAt) : undefined
        this.contractStartDate = this.data.contractStartDatetime ? new Date(this.data.contractStartDatetime) : undefined
        this.contractEndDate = this.data.contractEndDatetime ? new Date(this.data.contractEndDatetime) : undefined

        if (products) {
            const mainCachedProduct = products[0]
            const mainNewProduct = subscription.products?.[0]

            if (!mainNewProduct?.planPrice || !subscription.locale) {
                throw new ErrorInSubscriptionDataSimple('No planPrice found in main product')
            }

            this.products = [
                new SubscriptionLisaAndProduct(
                    mainCachedProduct.data,
                    this.currencySymbol,
                    this.locale,
                    new SubscriptionProductPlan(
                        mainNewProduct?.planPrice,
                        subscription.locale.code,
                        mainNewProduct.durationInMonths || 0,
                    ),
                    mainCachedProduct.priceDatas,
                ),
            ]
        } else {
            this.products = this.data.products?.map((product) => subscriptionProductFactory(product, this))
        }

        this.accessories = this.data.accessories?.map(
            (accessory) => new SubscriptionAccessory(accessory, this.currencySymbol),
        )
        this.deliveryMethods = [
            ...new Map((this.data.deliveryMethods || []).map((item) => [item.type, new DeliveryMethod(item)])).values(),
        ]

        if (this.data.deliveryMethod) {
            this.deliveryMethod = new DeliveryMethod(this.data.deliveryMethod)
        }

        if (this.data.customer) {
            this.customer = new Customer(this.data.customer)
        }

        if (!this.products?.[0]) {
            throw new ErrorInSubscriptionDataSimple('No main product found')
        }

        this.mainProduct = this.products[0]
    }

    getTranslation(name: string, options: TOptions = {}): string {
        // ANCHOR: Decision based on InvoicingSystem
        if (this.invoicingSystem === InvoicingSystem.LISAAND) {
            return t(`lisaAnd.${name}`, options)
        }

        if (exists(`${name}`)) {
            return t(`${name}`, options)
        }

        return ''
    }

    get invoicingSystem() {
        return this.data.invoicingSystem
    }

    get formattedUpgradeDate(): string {
        if (!this.data.upgradeDate) {
            return ''
        }

        return format(new Date(this.data.upgradeDate), 'dd.MM.yyyy')
    }

    get formattedCancelDate(): string {
        if (!this.data.cancelDate) {
            return ''
        }

        return format(new Date(this.data.cancelDate), 'dd.MM.yyyy')
    }

    get possibleSubscriptionMethods() {
        return this.data.possibleAuthenticationMethods
    }

    get uuid() {
        return this.data.uuid
    }

    get hasEnded() {
        return !!this.data.hasEnded
    }

    get hasTradeIn() {
        return (this.data?.nonQueryableMeta?.estimatedTradeInValue || 0) > 0
    }

    get status() {
        return this.data.status
    }

    get salesChannel() {
        return this.data.salesChannel
    }

    get currencySymbol() {
        return this.data.currency?.symbol || ''
    }

    get basketId() {
        return this.data.basketId
    }

    get redirectUrl() {
        return this.data.redirectUrl || ''
    }

    get country() {
        return this.data.country
    }

    get rejectedRedirectUrl() {
        return `${this.redirectUrl}/responseRejected?tid=${this.basketId}`
    }

    get acceptedRedirectUrl() {
        return `${this.redirectUrl}/responseAccepted?tid=${this.basketId}`
    }

    get abortedRedirectUrl() {
        return `${this.redirectUrl}/responseAborted?tid=${this.basketId}`
    }

    get possibleAuthenticationMethods() {
        return this.data.possibleAuthenticationMethods
    }

    get currentPlan() {
        return this.mainProduct?.plan
    }

    get currency() {
        return this.currencySymbol
    }

    get requireUpfrontPayment() {
        if (this.data.country?.invoicingSystem === InvoicingSystem.LISAAND) {
            return true
        }

        return this.data.country?.requireUpfrontPayment
    }

    get upfrontPaymentMonths() {
        return this.data.country?.upfrontPaymentNumberOfMonths || 1
    }

    get formattedSignedDate() {
        if (this.signedDate) {
            return format(this.signedDate, 'dd.MM.yyyy')
        }

        return '-'
    }

    get formattedContractStartDate() {
        if (this.contractStartDate) {
            return format(this.contractStartDate, 'dd.MM.yyyy')
        }

        return '-'
    }

    get formattedContractEndDate() {
        if (this.contractEndDate) {
            return format(this.contractEndDate, 'dd.MM.yyyy')
        } else if (this.lastUpdatedDate) {
            return format(this.lastUpdatedDate, 'dd.MM.yyyy')
        }

        return '-'
    }

    get oneTimePrices() {
        const accessories = (this.data.accessories || []).reduce<SubscriptionPrices>(
            (accumulator: SubscriptionPrices, current) => {
                // priceWithVat includes discount and quantity
                accumulator.grossPrice += current.priceWithVat

                // if (current.discount) {
                //     accumulator.grossPrice -= current.discount * quantity
                // }
                return accumulator
            },
            { grossPrice: 0, netPrice: 0 },
        )

        accessories.netPrice = calculateNet(accessories.grossPrice, this.data.country!.vatPercentage)

        return accessories
    }

    get currencyMonthly() {
        return `${this.currencySymbol}/${i18n.t('currency.perMonth')}`
    }

    get shippingPostalCode() {
        return this.data.shippingPostalCode
    }

    get shippingAddress() {
        return this.data.shippingAddress
    }

    get shippingCity() {
        return this.data.shippingCity
    }

    get shippingCountry() {
        return this.data.country?.code || ''
    }

    get differentShippingAddress() {
        return this.data.differentShippingAddress
    }

    get contractPdfUrl() {
        if (this.data.hasContract) {
            return `${process.env.APP_API_BASE_URL!}/api/v1/portal/customer/subscriptions/${this.uuid}/contract-pdf`
        }

        return
    }

    get invoicesUrl() {
        return `#/invoices`
    }

    get validationIframeUrl() {
        return this.customer?.data.lisaAndValidationIframeUrl || ''
    }

    get creditFailedReason() {
        return this.customer?.data.lisaAndValidationReason || undefined
    }

    static intersectingPlans = (allPlans: SubscriptionProductPlan[][]) => {
        const firstArray = allPlans[0]
        return allPlans.map(function (plansArray) {
            return plansArray.filter(function (plan) {
                for (const firstArrayPlan of firstArray) {
                    if (firstArrayPlan.uuid === plan.uuid) {
                        return true
                    }
                }

                return false
            })
        })
    }

    planCardProps(view: View): PlanCardProps[] {
        const plans = Subscription.getNorwayExclusiveMinimumTotalCost(this)

        if (!plans || !(plans instanceof Array)) {
            return []
        }

        return plans.map((plan) => {
            return plan.planCard(view)
        })
    }

    // NOTE: Nasty hack for Norway minimal total cost calculation
    static getNorwayExclusiveMinimumTotalCost(subscription: Subscription) {
        const allPlans = (subscription.products || []).map((product) => product.possiblePlans)
        const cleanedPlans = allPlans.filter((plan) => Array.isArray(plan))
        const keepIntersecting = Subscription.intersectingPlans(cleanedPlans)

        const firstProductPlans = keepIntersecting[0]

        if (!firstProductPlans) {
            return '-'
        }

        const plans = firstProductPlans.map((plan) => {
            const mainProduct = subscription.products?.[0]
            const clonedData = { ...subscription.products?.[0].data }
            clonedData.planPrice = {
                ...clonedData.planPrice,
                ...plan.data,
            } as PlanPriceDTO

            if (
                subscription.invoicingSystem === InvoicingSystem.LISAAND &&
                mainProduct?.constructor.name === 'SubscriptionLisaAndProduct'
            ) {
                return new SubscriptionLisaAndProduct(
                    clonedData as SubscriptionProductDTO,
                    subscription.currencySymbol,
                    subscription.locale,
                    plan,
                    (mainProduct as SubscriptionLisaAndProduct)?.priceDatas,
                    subscription.data.customer,
                )
            }

            return subscriptionProductFactory(clonedData as SubscriptionProductDTO, subscription)
        })

        // Minimum Total cost calculation logic - needs a bit of adjustment for Norway.
        // For Flex: no change
        // For Value: “Full Flex monthly price * 12”   OR   “Value monthly price * 24" <- whichever of those is lower, that should be shown.
        const isNorway = subscription.data.country?.code === 'NO'
        if (subscription.invoicingSystem !== InvoicingSystem.LISAAND && plans.length === 2 && isNorway) {
            const valuePlan = plans.find((plan) => plan.plan.code === PlanCode.VALUE)
            const flexPlan = plans.find((plan) => plan.plan.code === PlanCode.FLEX)

            if (valuePlan && flexPlan) {
                // Flex is calculated for 1 month as it can be cancelled after 1 month
                const flexFor12Months = flexPlan.minimalTotalCost * 12

                // Value is calculated for 12 months for Norway
                const valueFor24Months = valuePlan.minimalTotalCost * 2

                ;(valuePlan as SubscriptionValueProduct).setOverrideMinimalTotalCost(
                    Math.min(flexFor12Months, valueFor24Months),
                )
            }
        }

        return plans
    }

    getCopyProps(view: View): CopyProps | undefined {
        const imei = this.mainProduct.imei

        if (imei) {
            return {
                textToCopy: imei,
                onCopy: () => {
                    view.addNotification({
                        children: <p>{t('subscription.imeiCopy')}</p>,
                        isClosable: false,
                        isDefaultOpen: true,
                        duration: 4000,
                        type: 'info',
                    })
                },
            }
        }

        return undefined
    }

    getSubscriptionCardBaseProps(view: View): SubscriptionCardProps {
        if (this.hasEnded) {
            return {
                title: this.mainProduct.name,
                description: this.mainProduct.description,
                image: this.mainProduct.imageProps(40),
            }
        }

        let claimTag = this.mainProduct.claimStatusTagProps

        if ([SubscriptionStatus.SIGNED, SubscriptionStatus.ONLYFORUPGRADESIGNED].includes(this.data.status)) {
            claimTag = {
                text: t('subscription.tags.beingDelivered'),
                className: 'tag--black',
            }
        }

        if (this.data.childSubscriptionUuid && this.data.status === SubscriptionStatus.COMPLETED) {
            const child = view.state.portal.subscriptions?.find(
                (subscription) => subscription.uuid === this.data.childSubscriptionUuid,
            )

            if (child) {
                claimTag = {
                    text: t('subscription.tags.upgradeTo') + ' ' + child.mainProduct.nickname,
                    className: 'tag--black',
                }
            }
        }

        if (this.data.parentSubscriptionUuid && this.data.status !== SubscriptionStatus.COMPLETED) {
            const parent = view.state.portal.subscriptions?.find(
                (subscription) => subscription.uuid === this.data.parentSubscriptionUuid,
            )

            if (parent) {
                claimTag = {
                    text: t('subscription.tags.upgradeFor') + ' ' + parent.mainProduct.nickname,
                    className: 'tag--black',
                }
            }
        }

        if (
            this.data.pendingPaymentLink &&
            this.data.pendingPaymentLinkExpiresAt &&
            new Date(this.data.pendingPaymentLinkExpiresAt) > new Date()
        ) {
            return {
                title: this.mainProduct.name,
                claimTag: {
                    text: t('subscription.tags.pendingPayment'),
                    className: 'tag--black',
                },
                description: this.mainProduct.description,
                image: this.mainProduct.imageProps(40),
                paymentAction: {
                    link: this.data.pendingPaymentLink,
                    text: t('subscription.payPendingPayment'),
                },
            }
        }

        return {
            title: this.mainProduct.name,
            description: this.mainProduct.description,
            tagValue: this.mainProduct.nickname,
            claimTag,
            hasNickname: this.mainProduct.hasNickname,
            copy: this.getCopyProps(view),
            image: this.mainProduct.imageProps(40),
            imei: `IMEI ${this.mainProduct.imei ? this.mainProduct.imei : '-'}`,
            modalTriggerEditText: t('general.edit'),
            modalTriggerDefaultText: t('subscription.nickname.title'),
            nicknameModal: {
                title: t('subscription.nickname.title'),
                text: <p>{t('subscription.nickname.description')}</p>,
                input: {
                    label: t('subscription.nickname.nickname'),
                    id: `nickname-${this.mainProduct.uuid}`,
                    name: 'nickname',
                    value: view.state.portal.newNickname,
                    onChange: (value: string) => {
                        view.state.portal.newNickname = value
                        view.update()
                    },
                },
                cancelButton: {
                    text: t('general.cancel'),
                },
                saveButton: {
                    text: t('general.save'),
                    onClick: () => {
                        view.api.portal
                            .changeSubscriptionProductNickname(
                                this.uuid,
                                this.mainProduct.uuid,
                                view.state.portal.newNickname,
                            )
                            .then((success) => {
                                if (success) {
                                    this.mainProduct.nickname = view.state.portal.newNickname
                                }

                                view.update()
                            })
                            .catch(() => false)
                    },
                },
                triggerButton: {
                    text: t('subscription.nickname.title'),
                },
                onOpen: () => {
                    view.state.portal.newNickname = this.mainProduct.nickname
                    view.update()
                },
            },
        }
    }

    getSubscriptionCardDetailProps(view: View): SubscriptionCardProps {
        const base = this.getSubscriptionCardBaseProps(view)
        let actions: SubscriptionActionProps[] = []

        if (this.hasEnded) {
            if (this.contractPdfUrl) {
                actions.push({
                    url: this.contractPdfUrl,
                    text: t('subscription.viewContract'),
                    target: '_blank',
                })
            }

            actions.push({
                url: `#/invoices`,
                text: t('subscription.viewInvoices'),
            })
        } else if (this.mainProduct.claimCurrent) {
            actions = []
        } else {
            actions = [
                {
                    url: `#/subscriptions/${this.uuid}/manage`,
                    text: t('subscription.manageSubscription'),
                },
                {
                    url: `#/subscriptions/${this.uuid}/swap`,
                    text: t(
                        this.mainProduct.policyProvider === PolicyProvider.BOLTTECH
                            ? 'subscription.insuranceService'
                            : 'subscription.swapService',
                    ),
                    isDisabled: !this.permissions?.canSwap,
                },
            ]
        }

        return {
            ...base,
            actions,
            listItems: this.mainProduct.getSubscriptionCardDetailListItems(),
            isDetail: true,
        }
    }

    getSubscriptionCardProps(view: View, isSelected = false): SubscriptionCardProps {
        const base = this.getSubscriptionCardBaseProps(view)
        const actions = [
            {
                providerProps: {
                    isOpen: isSelected,
                    onOpen: () => {
                        window.location.href = this.hasEnded
                            ? `#/previous-devices/${this.uuid}`
                            : `#/subscriptions/${this.uuid}`
                    },
                    onClose: () => {
                        window.location.href = this.hasEnded ? `#/previous-devices` : `#/subscriptions`
                    },
                },
                children: <SubscriptionItem {...this.getSubscriptionCardDetailProps(view)} />,
                triggerButton: {
                    text: isSelected ? t('general.close') : t('general.view'),
                },
                closeButton: {
                    text: t('general.close'),
                },
                closeButtonMobile: {
                    text: t('general.back'),
                },
            },
        ]

        return {
            ...base,
            actions,
            listItems: this.mainProduct.getSubscriptionCardListItems(),
        }
    }

    getCancellationActionChoiceSelectProps = (view: View): ChoiceGroupProps | undefined => {
        if (this.currentPlan.code === PlanCode.VALUE) {
            return {
                id: 'state-choice',
                type: 'radio',
                label: t('subscription.cancel.stateChoice.title'),
                name: 'productResolution',
                value: view.state.portal.subscriptionClaim.productResolution,
                onChange: (value) => {
                    view.state.portal.subscriptionClaim.productResolution = value as ClaimProductResolution
                    view.update()
                },
                options: [
                    {
                        id: 'return-device',
                        label: t('subscription.cancel.stateChoice.returnDevice'),
                        value: ClaimProductResolution.RETURN,
                    },
                    {
                        id: 'keep-device',
                        label: t('subscription.cancel.stateChoice.keepDevice'),
                        value: ClaimProductResolution.KEEP,
                    },
                    {
                        id: 'upgrade-device',
                        label: t('subscription.cancel.stateChoice.upgradeDevice'),
                        value: ClaimProductResolution.UPGRADE,
                    },
                ],
            }
        }

        return undefined
    }

    renderCancellationDisclaimer = () => {
        if (this.currentPlan.code === PlanCode.VALUE) {
            const text =
                this.mainProduct.policyProvider === PolicyProvider.BOLTTECH
                    ? 'subscription.cancel.disclaimer.value.bolttech.content'
                    : 'subscription.cancel.disclaimer.value.content'
            const items = t(text, {
                returnObjects: true,
            })

            return (
                <div className="text">
                    <h2 className="text-large bold">{t('subscription.cancel.disclaimer.value.title')}</h2>
                    <ul>
                        {Array.isArray(items) &&
                            (items as string[]).map((item, index) => (
                                <li className="text-small" dangerouslySetInnerHTML={{ __html: item }} key={index}></li>
                            ))}
                    </ul>
                </div>
            )
        }

        if (this.currentPlan.code === PlanCode.FLEX) {
            const text =
                this.mainProduct.policyProvider === PolicyProvider.BOLTTECH
                    ? 'subscription.cancel.disclaimer.flex.bolttech.content'
                    : 'subscription.cancel.disclaimer.flex.content'
            const items = t(text, {
                returnObjects: true,
            })

            return (
                <div className="text">
                    <h2 className="text-large bold">{t('subscription.cancel.disclaimer.flex.title')} </h2>
                    <ul>
                        {Array.isArray(items) &&
                            (items as string[]).map((item, index) => (
                                <li className="text-small" dangerouslySetInnerHTML={{ __html: item }} key={index}></li>
                            ))}
                    </ul>
                </div>
            )
        }

        return <div />
    }

    private getUpgradeCard() {
        const contentText =
            this.mainProduct.policyProvider === PolicyProvider.BOLTTECH
                ? `subscription.upgrade.guide.${this.currentPlan.code}.bolttech.content`
                : `subscription.upgrade.guide.${this.currentPlan.code}.content`
        const cardItems = t(contentText, {
            returnObjects: true,
            months: this.mainProduct.data.durationInMonths,
        })
        const steps = t(`subscription.upgrade.guide.${this.currentPlan.code}.steps`, {
            returnObjects: true,
            months: this.mainProduct.data.durationInMonths,
        })

        return {
            accordionItems: [
                {
                    label: t(`subscription.upgrade.guide.${this.currentPlan.code}.title`),
                    id: 'upgrade-card-accordion-item-1',
                    children: (
                        <ol className="list list--compact">
                            {Array.isArray(steps) &&
                                (steps as string[]).map((listItem, index) => (
                                    <li
                                        key={index}
                                        dangerouslySetInnerHTML={{
                                            __html: listItem,
                                        }}
                                    />
                                ))}
                        </ol>
                    ),
                },
            ],
            button: {
                isDisabled: !this.permissions?.canUpgrade,
                text: t(`subscription.upgrade.guide.${this.currentPlan.code}.action`),
                url: t(`subscription.upgrade.guide.${this.currentPlan.code}.url`),
                onClick: () => {
                    capture('CTA clicked', { cta: 'upgrade' })
                },
            },
            content: (
                <div>
                    {Array.isArray(cardItems) &&
                        (cardItems as string[]).map((paragraph, index) => (
                            <p key={index} dangerouslySetInnerHTML={{ __html: paragraph }}></p>
                        ))}
                </div>
            ),
            image: {
                srcset: t(`subscription.upgrade.guide.${this.currentPlan.code}.image`),
                width: 442,
                alt: 'upgrade card image',
            },
            title: t(`subscription.upgrade.guide.${this.currentPlan.code}.title`),
        }
    }

    private getCancelCard() {
        const text =
            this.mainProduct.policyProvider === PolicyProvider.BOLTTECH
                ? `subscription.cancel.guide.${this.currentPlan.code}.bolttech.content`
                : `subscription.cancel.guide.${this.currentPlan.code}.content`
        const cardItems = t(text, {
            returnObjects: true,
            months: this.currentPlan.duration,
        })
        const steps = t(`subscription.cancel.guide.${this.currentPlan.code}.steps`, { returnObjects: true })

        return {
            accordionItems: [
                {
                    label: t(`subscription.cancel.guide.${this.currentPlan.code}.stepsTitle`),
                    id: 'upgrade-card-accordion-item-1',
                    children: (
                        <ol className="list list--compact">
                            {Array.isArray(steps) &&
                                (steps as string[]).map((listItem, index) => (
                                    <li
                                        key={index}
                                        dangerouslySetInnerHTML={{
                                            __html: listItem,
                                        }}
                                    ></li>
                                ))}
                        </ol>
                    ),
                },
            ],
            button: {
                isDisabled: !this.permissions?.canCancel,
                text: t(`subscription.cancel.guide.${this.currentPlan.code}.action`),
                url: `#/subscriptions/${this.uuid}/cancel`,
            },
            content: (
                <div>
                    {Array.isArray(cardItems) &&
                        (cardItems as string[]).map((paragraph, index) => (
                            <p key={index} dangerouslySetInnerHTML={{ __html: paragraph }}></p>
                        ))}
                </div>
            ),
            image: {
                srcset: t(`subscription.cancel.guide.${this.currentPlan.code}.image`),
                width: 442,
                alt: 'upgrade card image',
            },
            title: t(`subscription.cancel.guide.${this.currentPlan.code}.title`),
        }
    }

    getSubscriptionActionCards() {
        const cards: UpgradeCardProps[] = []

        cards.push(this.getUpgradeCard())
        cards.push(this.getCancelCard())

        return cards
    }

    getSubscriptionNotificationCards() {
        const cards: NotificationCardProps[] = []

        if (!this.permissions?.canUpgrade) {
            cards.push({
                title: t('subscription.upgrade.unavailableNotification.title'),
                content: t('subscription.upgrade.unavailableNotification.content'),
                link: {
                    text: t('subscription.upgrade.unavailableNotification.linkText'),
                    href: t('subscription.upgrade.unavailableNotification.linkUrl'),
                },
            })
        }

        if (!this.permissions?.canCancel) {
            const isEligibleForCancelByDate = !!(typeof this.permissions?.parts?.canCancel !== 'boolean'
                ? this.permissions?.parts?.canCancel?.isEligibleForCancelByDate
                : true)
            const remorseReturnDays =
                this.country?.remorseReturnDays !== undefined
                    ? this.country?.remorseReturnDays
                    : DEFAULT_REMORSE_RETURN_DAYS

            let content = !isEligibleForCancelByDate
                ? t(`subscription.cancel.guide.${this.currentPlan.code}.eligibleNotificationText`, {
                      days: remorseReturnDays,
                  })
                : t('subscription.cancel.unavailableNotification.content')

            if (
                typeof this.permissions?.parts?.canCancel === 'object' &&
                this.permissions?.parts?.canCancel.hasAutomaticCancelClaim
            ) {
                content = t('subscription.cancel.unavailableNotification.automaticClaim')
            }

            cards.push({
                title: t('subscription.cancel.unavailableNotification.title'),
                content,
                link: {
                    text: t('subscription.cancel.unavailableNotification.linkText'),
                    href: t('subscription.cancel.unavailableNotification.linkUrl'),
                },
            })
        }

        if (!this.permissions?.canSwap) {
            cards.push({
                title: t('subscription.swap.unavailableNotification.title'),
                content: t('subscription.swap.unavailableNotification.content'),
                link: {
                    text: t('subscription.swap.unavailableNotification.linkText'),
                    href: t('subscription.swap.unavailableNotification.linkUrl'),
                },
            })
        }

        return cards
    }

    updateDeliveryMethod(methodUuid: string) {
        this.deliveryMethod = this.deliveryMethods.find((method) => method.uuid === methodUuid)
    }

    canSign(stateClientFormData: State) {
        return (
            this.customer &&
            ((this.data.creditCheck === 'SUCCESSFUL' && this.customer.personalNumber) ||
                (this.data.creditCheck === 'ONLYFORUPGRADE' &&
                    this.customer.personalNumber &&
                    this.data.status === 'ONLYFORUPGRADE') ||
                (stateClientFormData.creditCheckPassed && stateClientFormData.formData.personalNumber))
        )
    }

    hasCreditCheckFailed() {
        return this.data.creditCheck === 'FAILED'
    }

    canRunCreditCheck() {
        return (
            this.customer &&
            (this.data.creditCheck === 'PENDING' ||
                (this.data.creditCheck === 'SUCCESSFUL' && !this.customer.personalNumber))
        )
    }

    canValidate() {
        return (
            !!this.customer?.data.lisaAndValidationIframeUrl &&
            this.customer?.data.lisaAndValidationStatus === 'IN_PROGRESS' &&
            this.data.creditCheck === 'PENDING'
        )
    }

    canEditName() {
        return this.data.canEditName
    }

    findClaim(claimId: string) {
        return this.mainProduct.claims.find((claim) => claim.visualID === claimId)
    }

    getClaimPayUrl(claimId: number | string) {
        return `${process.env.APP_API_BASE_URL!}/api/v1/portal/customer/subscriptions/${
            this.data.uuid
        }/claims/${claimId}/pay`
    }
}
